import React, { useState } from 'react'
import axios from 'axios'
import { DebounceInput } from 'react-debounce-input'
import Modal from '../shared/modal'
import { t } from '../../i18n'
import { PortionType, RecipeStatus, RecipeTask } from './types'
import { useActiveRecipes } from '../../hooks/useActiveRecipes'

interface RecipeListItem {
  id: number,
  name: string,
  portion_type: PortionType,
  image_url: string | null,
  status: RecipeStatus
}

interface Props {
  portionType: PortionType,
  currentRecipeId: number | null,
  onSelect: (tasks: Record<string, RecipeTask[]>) => void,
  onClose: () => void
}

const CopyFromRecipeModal = ({ portionType, currentRecipeId, onSelect, onClose }: Props) => {
  const [searchQuery, setSearchQuery] = useState('')
  const [fetchingRecipe, setFetchingRecipe] = useState(false)
  const [selectError, setSelectError] = useState<string | null>(null)

  const { recipes, sentinelRef, rootRef, loading, hasMoreRecordsInScope, error: loadError } =
    useActiveRecipes<RecipeListItem>({ query: searchQuery, type: portionType })

  const visibleRecipes = recipes.filter((recipe) => recipe.id !== currentRecipeId)
  const showInitialLoading = loading && recipes.length === 0

  const handleSelect = (recipeId: number) => {
    setFetchingRecipe(true)
    setSelectError(null)

    axios.get(`/api/internal/recipes/${recipeId}`)
      .then(({ data }) => {
        onSelect(data.recipe_tasks)
      })
      .catch(() => {
        setSelectError(t('recipes.copy_from_recipe.fetch_error'))
        setFetchingRecipe(false)
      })
  }

  return (
    <Modal headerText={t('recipes.copy_from_recipe.title')} widen onClose={onClose}>
      <div className='copy-from-recipe-modal'>
        <div className='form-group mb-3'>
          <DebounceInput
            className='form-control'
            placeholder={t('recipes.copy_from_recipe.search_placeholder')}
            value={searchQuery}
            debounceTimeout={500}
            onChange={(e) => setSearchQuery(e.target.value)}
            disabled={fetchingRecipe}
          />
        </div>

        {selectError && <p className='text-danger'>{selectError}</p>}
        {loadError && recipes.length === 0 && (
          <p className='text-danger'>{t('recipes.copy_from_recipe.load_error')}</p>
        )}

        {showInitialLoading && <p className='text-secondary'>{t('recipes.copy_from_recipe.loading')}</p>}

        {!showInitialLoading && !loadError && visibleRecipes.length === 0 && !hasMoreRecordsInScope && (
          <p className='text-secondary'>{t('recipes.copy_from_recipe.no_recipes')}</p>
        )}

        {!showInitialLoading && (visibleRecipes.length > 0 || (hasMoreRecordsInScope && !loadError)) && (
          <div className='copy-from-recipe-list' ref={rootRef} style={{ maxHeight: '50vh', overflowY: 'auto' }}>
            {visibleRecipes.map((recipe) => (
              <button
                key={recipe.id}
                type='button'
                className='btn btn-light w-100 text-start mb-2 d-flex align-items-center'
                onClick={() => handleSelect(recipe.id)}
                disabled={fetchingRecipe}
              >
                {recipe.image_url && (
                  <img
                    src={recipe.image_url}
                    alt=''
                    style={{
                      width: 40,
                      height: 40,
                      objectFit: 'cover',
                      borderRadius: '50%',
                      marginRight: 12
                    }}
                  />
                )}
                <span>{recipe.name}</span>
              </button>
            ))}
            {hasMoreRecordsInScope && <div ref={sentinelRef} aria-hidden="true" style={{ height: 1, overflowAnchor: 'none' }} />}
            {loading && recipes.length > 0 && (
              <div className="d-flex justify-content-center py-2" style={{ overflowAnchor: 'none' }}>
                <span className="spinner-border spinner-border-sm ms-2" role="status" aria-hidden="true" />
              </div>
            )}
          </div>
        )}

        <div className='buttons mt-3 d-flex justify-content-end'>
          <button type='button' className='btn btn-secondary' onClick={onClose} disabled={fetchingRecipe}>
            {t('cancel')}
          </button>
        </div>
      </div>
    </Modal>
  )
}

export default CopyFromRecipeModal
